import { useEffect, useState } from "react";
import { NumericFormat } from "react-number-format";
import currencies from "../../constants/CurrencyData";

type NumericFormatFieldDebounceProps = {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  errorMessage?: string;
  className?: string;
  name?: string;
  autoFocus?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  currency?: string;
  isPercentage?: boolean;
  delay?: number;
  testId?: string;
};

const NumericFormatFieldDebounce = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  errorMessage,
  className,
  name,
  autoFocus,
  required,
  min,
  max,
  currency,
  isPercentage = false,
  delay = 400,
  testId,
}: NumericFormatFieldDebounceProps) => {
  const [internalValue, setInternalValue] = useState(value || "");

  const currencyData = currencies[currency] || currencies["USD"];
  const decimalScale = isPercentage ? 0 : currencyData.decimal_digits || 2;

  useEffect(() => {
    setInternalValue(value || "");
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (internalValue !== (value || "")) {
        if (onChange) onChange(internalValue);
      }
    }, delay);
    return () => clearTimeout(handler);
  }, [internalValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = () => {
    if (onChange) onChange(internalValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      if (onChange) onChange(internalValue);
    }
  };

  return (
    <div className={`w-full ${className || ""}`}>
      {label ? (
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {label}
          {required ? <span className="text-red-600"> *</span> : null}
        </label>
      ) : null}
      <div className="relative">
        <NumericFormat
          data-testid={testId}
          name={name}
          disabled={disabled}
          autoFocus={autoFocus}
          value={internalValue}
          onValueChange={(values) => {
            const { value: rawValue } = values;
            setInternalValue(rawValue);
          }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          prefix={isPercentage ? undefined : `${currencyData.symbol} `}
          suffix={isPercentage ? " %" : undefined}
          thousandSeparator={isPercentage ? false : ","}
          decimalSeparator="."
          decimalScale={decimalScale}
          fixedDecimalScale={false}
          allowNegative={false}
          inputMode={isPercentage ? "numeric" : "text"}
          isAllowed={(values) => {
            if (!isPercentage) return true;
            const { floatValue } = values;
            return (
              floatValue === undefined ||
              (Number.isInteger(floatValue) && floatValue >= min && floatValue <= max)
            );
          }}
          placeholder={placeholder}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-800 placeholder:text-gray-500 focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-600 disabled:bg-gray-50 disabled:text-gray-400"
        />
      </div>
      {errorMessage ? <p className="mt-1 text-xs text-red-600">{errorMessage}</p> : null}
    </div>
  );
};

export default NumericFormatFieldDebounce;
